import { useState } from 'react'
import { X, Plus, AlertTriangle, Lock } from 'lucide-react'
import { toast } from 'sonner'
import type { WireArticleFull, WireRevisionNotice, WireRevisionType } from '../../api/types'
import { useWireAuth } from '../../auth/useWireAuth'
import { useAuthz } from '../../auth/useAuthz'
import { CmtWrapper } from '../common/cmt'
import type { CmtResult } from '../common/cmt'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const REVISION_TYPE_LABELS: Record<WireRevisionType, string> = {
  'substantive-revision':     'Substantive Revision',
  'administrative-revision':  'Administrative Revision',
  'compliance-revision':      'Compliance Revision',
  'substantive-recall':       'Substantive Recall',
  'administrative-recall':    'Administrative Recall',
  'compliance-recall':        'Compliance Recall',
}

function fmtDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: 'medium',
      timeStyle: 'short',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

// ─── Existing notice row ──────────────────────────────────────────────────────

function RevisionNoticeRow({ notice }: { notice: WireRevisionNotice }) {
  return (
    <li className="rounded border border-line bg-fill p-3">
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <span className="rounded border border-line bg-canvas px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-fg-secondary">
          {REVISION_TYPE_LABELS[notice.type]}
        </span>
        <span className="text-[10px] text-fg-secondary">{fmtDate(notice.revisedAt)}</span>
        <span className="text-[10px] text-fg-secondary">· {notice.revisedByName}</span>
      </div>
      {notice.descriptionClassification && (
        <p className="mb-1 font-mono text-[10px] text-fg-secondary">
          ({[notice.descriptionClassification, ...(notice.descriptionDisseminationControls ?? [])].join('//')})
        </p>
      )}
      <p className="text-xs leading-relaxed text-fg">{notice.description}</p>
    </li>
  )
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface Props {
  article: WireArticleFull
  open: boolean
  onClose: () => void
  onUpdate: (patch: Partial<WireArticleFull>) => void
}

// ─── ArticleRevisionsDrawer ───────────────────────────────────────────────────

export function ArticleRevisionsDrawer({ article, open, onClose, onUpdate }: Props) {
  const { user } = useWireAuth()
  const { isEditorialManager, isPostProduction } = useAuthz()
  const canIssueRevision = isEditorialManager || isPostProduction

  const [type, setType]                       = useState<WireRevisionType>('substantive-revision')
  const [description, setDescription]         = useState('')
  const [descriptionCmt, setDescriptionCmt]   = useState<CmtResult | undefined>(undefined)

  if (!open) return null

  const revisions = article.revisions ?? []

  function handleAdd() {
    if (!description.trim()) return
    const notice: WireRevisionNotice = {
      type,
      description: description.trim(),
      descriptionClassification: descriptionCmt?.classification,
      descriptionDisseminationControls: descriptionCmt?.disseminationControls?.length ? descriptionCmt.disseminationControls : undefined,
      revisedAt: new Date().toISOString(),
      revisedByName: user?.name ?? user?.dn ?? user?.sub ?? 'Unknown',
    }
    onUpdate({ revisions: [...revisions, notice] })
    setDescription('')
    setDescriptionCmt(undefined)
    setType('substantive-revision')
    toast.success('Revision notice added.')
  }

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-30 bg-black/30"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer panel */}
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Revisions and recall"
        className="fixed right-0 top-[3rem] z-40 flex h-[calc(100vh-3rem)] w-[480px] flex-col bg-surface shadow-2xl"
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-line px-6 py-4">
          <div>
            <p className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
              Article
            </p>
            <p className="mt-0.5 text-sm font-semibold text-fg">Revisions &amp; Recall</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="ml-4 shrink-0 rounded p-1 text-fg-secondary transition-colors hover:bg-fill hover:text-fg"
            aria-label="Close revisions panel"
          >
            <X size={16} />
          </button>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-8">

            {/* ── Published revision notices ──────────────────────────── */}
            <section>
              <div className="mb-3 flex items-center gap-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                  Published Revision Notices
                </p>
                {revisions.length > 0 && (
                  <span className="rounded-full bg-fill px-1.5 py-0.5 text-[10px] font-bold text-fg-secondary border border-line">
                    {revisions.length}
                  </span>
                )}
              </div>
              {revisions.length === 0 ? (
                <p className="text-xs text-fg-secondary">No revision notices recorded yet.</p>
              ) : (
                <ol className="m-0 list-none space-y-3 p-0">
                  {revisions.map((r, i) => (
                    <RevisionNoticeRow key={i} notice={r} />
                  ))}
                </ol>
              )}
            </section>

            <div className="border-t border-line" />

            {/* ── Permission banner ────────────────────────────────── */}
            {!canIssueRevision && (
              <div className="flex items-start gap-2 rounded-md border px-3 py-2.5 text-[11px] leading-snug" style={{ backgroundColor: 'var(--gray-2)', borderColor: 'var(--gray-6)', color: 'var(--gray-11)' }}>
                <Lock size={12} className="mt-0.5 shrink-0" aria-hidden="true" />
                <p>You don't have permission to add revision notices or record recalls. Contact an Editorial Manager or Post-Production reviewer.</p>
              </div>
            )}

            {/* ── Add new revision notice ─────────────────────────────── */}
            <section>
              <p className="mb-4 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                Add Revision Notice
              </p>
              <fieldset disabled={!canIssueRevision} className="space-y-4">

                <div>
                  <label
                    htmlFor="rev-type"
                    className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary"
                  >
                    Type
                  </label>
                  <select
                    id="rev-type"
                    value={type}
                    onChange={e => setType(e.target.value as WireRevisionType)}
                    className="w-full rounded border border-line bg-fill px-3 py-2 text-sm text-fg focus:border-accent-primary-line focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="substantive-revision">Substantive Revision</option>
                    <option value="administrative-revision">Administrative Revision</option>
                    <option value="compliance-revision">Compliance Revision</option>
                    <option value="substantive-recall">Substantive Recall</option>
                    <option value="administrative-recall">Administrative Recall</option>
                    <option value="compliance-recall">Compliance Recall</option>
                  </select>
                </div>

                <div>
                  <label
                    htmlFor="rev-class"
                    className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary"
                  >
                    Description Classification
                  </label>
                  <CmtWrapper
                    value={descriptionCmt}
                    onChange={setDescriptionCmt}
                    compact
                  />
                </div>

                <div>
                  <label
                    htmlFor="rev-description"
                    className="mb-1 block text-[10px] font-bold uppercase tracking-widest text-fg-secondary"
                  >
                    Description
                  </label>
                  <textarea
                    id="rev-description"
                    value={description}
                    onChange={e => setDescription(e.target.value)}
                    rows={4}
                    placeholder="Describe what was corrected, updated, or clarified for readers…"
                    className="w-full resize-none rounded border border-line bg-fill px-3 py-2 text-sm text-fg focus:border-accent-primary-line focus:outline-none disabled:cursor-not-allowed disabled:opacity-50"
                  />
                </div>

                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={!description.trim()}
                  className="flex w-full items-center justify-center gap-1.5 rounded border border-line bg-solid px-3 py-2 text-xs font-semibold text-white transition-colors hover:bg-solid-hover disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Plus size={12} aria-hidden="true" />
                  Add Revision Notice
                </button>

              </fieldset>
            </section>

            <div className="border-t border-line" />

            {/* ── Recall ─────────────────────────────────────────────── */}
            <section>
              <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
                Recall Article
              </p>
              <p className="mb-4 text-xs text-fg-secondary">
                Recording a recall removes this article from reader view immediately and flags it for post-publication review.
              </p>
              <button
                type="button"
                onClick={() => toast.info('Recall — not yet implemented.')}
                disabled={!canIssueRevision}
                className="flex w-full items-center justify-center gap-1.5 rounded border border-red-600 px-3 py-2 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-red-950"
              >
                <AlertTriangle size={12} aria-hidden="true" />
                Record Recall
              </button>
            </section>

          </div>
        </div>
      </aside>
    </>
  )
}
